import { BodyLink } from '@jouzen/ecom-components/v3/BodyLink';
import { LayoutGrid } from '@jouzen/ecom-components/v3/LayoutGrid';
import { Typography } from '@jouzen/ecom-components/v3/Typography';
import { TypographyRhythm } from '@jouzen/ecom-components/v3/TypographyRhythm';
import { useLocale, useTranslations } from 'next-intl';
import type { ReactNode } from 'react';

import LegalHeading from '@/app/components/LegalComponents/LegalHeading';
import LegalListItem from '@/app/components/LegalComponents/LegalListItem';
import LegalOrderedList from '@/app/components/LegalComponents/LegalOrderedList';
import LegalParagraph from '@/app/components/LegalComponents/LegalParagraph';
import PageLayout from '@/app/components/PageLayout';
import RichText from '@/app/components/RichText';

interface LinkProps {
  readonly chunks: ReactNode;
  readonly selectedLocale: string;
}

const cookiepolicylink = ({ chunks, selectedLocale }: LinkProps) => (
  <BodyLink href={`/${selectedLocale}/cookie-policy`}>{chunks}</BodyLink>
);

const tandclink = ({ chunks, selectedLocale }: LinkProps) => (
  <BodyLink href={`/${selectedLocale}/terms-and-conditions`}>
    {chunks}
  </BodyLink>
);

const deviceapplicationlink = (chunks: ReactNode) => (
  <BodyLink href="#device-application-section">{chunks}</BodyLink>
);

const onlinestorelink = (chunks: ReactNode) => (
  <BodyLink href="#website-online-store-section">{chunks}</BodyLink>
);

const datasharinglink = (chunks: ReactNode) => (
  <BodyLink href="#data-sharing-and-disclosures-section">{chunks}</BodyLink>
);

const profilelink = (chunks: ReactNode) => (
  <BodyLink
    external
    href="https://cloud.ouraring.com/profile"
    target="_blank"
    rel="noreferrer"
  >
    {chunks}
  </BodyLink>
);

const dataprivacylink = (chunks: ReactNode) => (
  <BodyLink external href="https://www.dataprivacyframework.gov/">
    {chunks}
  </BodyLink>
);

const supportlink = (chunks: ReactNode) => (
  <BodyLink
    external
    href="https://support.ouraring.com/hc/en-us/articles/360025586673-How-Oura-Protects-Your-Data#h_01G3S1RC2P0WBPSD7ZZ946QVH9"
  >
    {chunks}
  </BodyLink>
);

const cloudlink = (chunks: ReactNode) => (
  <BodyLink
    external
    href="https://cloud.ouraring.com"
    target="_blank"
    rel="noreferrer"
  >
    {chunks}
  </BodyLink>
);

const PrivacyPolicyOuraHealth = () => {
  const selectedLocale = useLocale();
  const t = useTranslations();

  return (
    <PageLayout
      pageName="Privacy Policy"
      containerProps={{ name: 'privacy-policy', padding: 'both' }}
      footerProps={{
        wrapperClassName: 'bg-white',
      }}
    >
      <LayoutGrid data-cy="privacy-policy-page">
        <TypographyRhythm className="col-main">
          <Typography
            Element="h1"
            variant="h1"
            className="mb-4"
            data-cy="privacy-policy-title"
          >
            {t('privacy_policy_oh_title')}
          </Typography>

          <Typography weight="bold" className="mb-8">
            {t('release_april_21_2025')}
          </Typography>

          {/* OURAS COMMITMENT TO PRIVACY */}
          <LegalHeading
            level="I" // I
            tx="privacy_policy_commitment_heading"
          >
            <LegalParagraph tx="privacy_policy_commitment_p1" />
            <LegalParagraph tx="privacy_policy_commitment_p2" />
          </LegalHeading>

          {/* ABOUT THIS PRIVACY POLICY */}
          <LegalHeading
            level="I" // II
            tx="privacy_policy_oh_about_heading"
          >
            <LegalParagraph tx="privacy_policy_about_p1_oct_2024" />
          </LegalHeading>

          {/* WHY DOES OURA PROCESS YOUR PERSONAL DATA? */}
          <LegalHeading
            level="I" // III
            tx="privacy_policy_oh_personal_heading"
          >
            <LegalParagraph tx="privacy_policy_oh_personal_p1" />

            <LegalHeading
              level="A" // II.A
              tx="privacy_policy_oh_personal_device_heading"
              id="device-application-section"
            >
              <LegalHeading
                level="i" // II.A.i
                tx="privacy_policy_oh_personal_device_purposes_heading"
              />
              <LegalParagraph tx="privacy_policy_oh_personal_device_purposes_p1" />

              <LegalOrderedList spacing="double">
                <LegalListItem tx="privacy_policy_personal_device_purposes_item1_oct_2024" />
                <LegalListItem tx="privacy_policy_personal_device_purposes_item2_oct_2024" />
                <LegalListItem tx="privacy_policy_personal_device_purposes_item3_oct_2024" />
                <LegalListItem tx="privacy_policy_personal_device_purposes_item7_oct_2024" />
                <LegalListItem tx="privacy_policy_personal_device_purposes_item8_oct_2024" />
                <LegalListItem
                  tx="privacy_policy_personal_device_purposes_item4_oct_2024"
                  values={{
                    a: (chunks: ReactNode) =>
                      cookiepolicylink({ chunks, selectedLocale }),
                  }}
                />
                <LegalListItem tx="privacy_policy_personal_device_purposes_item5_oct_2024" />
                <LegalListItem tx="privacy_policy_personal_device_purposes_item6_oct_2024" />
              </LegalOrderedList>

              <LegalHeading
                level="i" // II.A.ii
                tx="privacy_policy_oh_personal_device_basis_heading"
              />
              <LegalParagraph tx="privacy_policy_oh_personal_device_basis_p1" />

              <LegalOrderedList spacing="double">
                <LegalListItem tx="privacy_policy_oh_personal_device_basis_item1" />
                <LegalListItem tx="privacy_policy_oh_personal_device_basis_item2" />
                <LegalListItem tx="privacy_policy_oh_personal_device_basis_item3" />
                <LegalListItem tx="privacy_policy_oh_personal_device_basis_item4" />
              </LegalOrderedList>

              <LegalHeading
                level="i" // II.A.iii
                tx="privacy_policy_oh_personal_device_source_heading"
              />
              <LegalParagraph tx="privacy_policy_oh_personal_device_source_p1" />
              <LegalParagraph tx="privacy_policy_oh_personal_device_source_p2" />

              <LegalOrderedList>
                <LegalListItem tx="privacy_policy_personal_device_source_item1_oct_2024" />
                <LegalListItem tx="privacy_policy_personal_device_source_item2_oct_2024" />
                <LegalListItem tx="privacy_policy_personal_device_source_item3_oct_2024" />
                <LegalListItem tx="privacy_policy_personal_device_source_item4_oct_2024" />
                <LegalListItem tx="privacy_policy_personal_device_source_item5_oct_2024" />
                <LegalListItem tx="privacy_policy_personal_device_source_item6_oct_2024" />
              </LegalOrderedList>

              <LegalParagraph tx="privacy_policy_personal_device_source_p3_oct_2024" />
              <LegalParagraph
                tx="privacy_policy_personal_device_source_p4_oct_2024"
                values={{
                  a: (chunks: ReactNode) =>
                    tandclink({ chunks, selectedLocale }),
                }}
              />

              <LegalHeading
                level="i" // II.A.iv
                tx="oura_platform"
                id="oura-teams-section"
              >
                <LegalParagraph tx="privacy_policy_oura_teams_p1_oct_2024" />
                <LegalParagraph tx="privacy_policy_oura_teams_p2_oct_2024" />
                <LegalParagraph tx="privacy_policy_oura_teams_p3_oct_2024" />

                <LegalParagraph
                  tx="privacy_policy_oura_teams_p4_oct_2024"
                  className="mb-0 font-bold"
                />
                <LegalOrderedList className="mt-0">
                  <LegalListItem tx="name" />
                  <LegalListItem tx="privacy_policy_email_address_item1_oct_2024" />
                  <LegalListItem tx="privacy_policy_gender_item2_oct_2024" />
                  <LegalListItem tx="privacy_policy_birthdate_item3_oct_2024" />
                  <LegalListItem tx="privacy_policy_height_weight_item4_oct_2024" />
                  <LegalListItem tx="privacy_policy_activities_item5_oct_2024" />
                  <LegalListItem tx="privacy_policy_notes_tags_item6_oct_2024" />
                </LegalOrderedList>

                <LegalParagraph
                  tx="privacy_policy_oura_teams_p5_oct_2024"
                  className="mb-0 font-bold"
                />
                <LegalOrderedList className="mt-0">
                  <LegalListItem tx="privacy_policy_heart_rate_item1_oct_2024" />
                  <LegalListItem tx="privacy_policy_movement_item2_oct_2024" />
                  <LegalListItem tx="privacy_policy_temperature_item3_oct_2024" />
                  <LegalListItem tx="privacy_policy_sleep_item4_oct_2024" />
                  <LegalListItem tx="privacy_policy_sleep_phases_item5_oct_2024" />
                  <LegalListItem tx="privacy_policy_activity_data_item6_oct_2024" />
                  <LegalListItem tx="privacy_policy_generated_tags_item7_oct_2024" />
                </LegalOrderedList>

                <LegalParagraph
                  tx="privacy_policy_oura_teams_p6_oct_2024"
                  className="mb-0 font-bold"
                />
                <LegalOrderedList className="mt-0">
                  <LegalListItem tx="privacy_policy_user_id_item1_oct_2024" />
                  <LegalListItem tx="privacy_policy_ip_address_item2_oct_2024" />
                  <LegalListItem tx="privacy_policy_metadata_item3_oct_2024" />
                </LegalOrderedList>

                <LegalParagraph tx="privacy_policy_oura_teams_p7_oct_2024" />
                <LegalParagraph
                  tx="privacy_policy_oura_teams_p8_oct_2024"
                  values={{ a: profilelink }}
                />
                <LegalParagraph tx="privacy_policy_oura_teams_p9_oct_2024" />
              </LegalHeading>
            </LegalHeading>

            <LegalHeading
              level="A" // II.B
              tx="privacy_policy_oh_personal_store_heading"
              id="website-online-store-section"
            >
              <LegalHeading
                level="i" // II.B.i
                tx="privacy_policy_oh_personal_store_purposes_heading"
              />

              <LegalParagraph tx="privacy_policy_oh_personal_store_purposes_p1" />
              <LegalOrderedList spacing="double">
                <LegalListItem tx="privacy_policy_personal_store_purposes_item1_oct_2024" />
                <LegalListItem tx="privacy_policy_personal_store_purposes_item2_oct_2024" />
                <LegalListItem tx="privacy_policy_personal_store_purposes_item3_oct_2024" />
                <LegalListItem tx="privacy_policy_personal_store_purposes_item4_oct_2024" />
                <LegalListItem tx="privacy_policy_personal_store_purposes_item8_oct_2024" />
                <LegalListItem
                  tx="privacy_policy_personal_store_purposes_item5_oct_2024"
                  values={{
                    a: (chunks: ReactNode) =>
                      cookiepolicylink({ chunks, selectedLocale }),
                  }}
                />
                <LegalListItem tx="privacy_policy_personal_store_purposes_item6_oct_2024" />
              </LegalOrderedList>

              <LegalHeading
                level="i" // II.B.ii
                tx="privacy_policy_oh_personal_store_basis_heading"
              />

              <LegalParagraph tx="privacy_policy_oh_personal_store_basis_p1" />
              <LegalOrderedList spacing="double">
                <LegalListItem tx="privacy_policy_oh_personal_store_basis_item1" />
                <LegalListItem tx="privacy_policy_oh_personal_store_basis_item2" />
                <LegalListItem tx="privacy_policy_oh_personal_store_basis_item3" />
                <LegalListItem tx="privacy_policy_oh_personal_store_basis_item4" />
              </LegalOrderedList>

              <LegalHeading
                level="i" // II.B.iii
                tx="privacy_policy_oh_personal_store_source_heading"
              />

              <LegalParagraph tx="privacy_policy_oh_personal_store_source_p1" />
              <LegalOrderedList>
                <LegalListItem tx="privacy_policy_oh_personal_store_source_item1" />
                <LegalListItem tx="privacy_policy_oh_personal_store_source_item2" />
                <LegalListItem tx="privacy_policy_oh_personal_store_source_item3" />
                <LegalListItem tx="privacy_policy_oh_personal_store_source_item4" />
              </LegalOrderedList>
            </LegalHeading>

            <LegalHeading
              level="A" // II.C
              tx="privacy_policy_oh_personal_california_heading"
            >
              <LegalHeading
                level="i" // II.C.i
                tx="privacy_policy_oh_personal_california_ccpa_heading"
              />
              <LegalParagraph tx="privacy_policy_oh_personal_california_ccpa_p1" />
              <LegalParagraph tx="privacy_policy_oh_personal_california_ccpa_p2" />

              <LegalHeading
                level="i" // II.C.ii
                tx="privacy_policy_oh_personal_california_info_heading"
              />
              <LegalParagraph tx="privacy_policy_oh_personal_california_info_p1_oct_2024" />
              <LegalParagraph tx="privacy_policy_oh_personal_california_info_p2_oct_2024" />

              <LegalOrderedList>
                <LegalListItem
                  tx="privacy_policy_oh_personal_california_info_item1"
                  values={{ a: deviceapplicationlink }}
                />
                <LegalListItem
                  tx="privacy_policy_oh_personal_california_info_item2"
                  values={{ a: onlinestorelink }}
                />
                <LegalListItem
                  tx="privacy_policy_oh_personal_california_info_item3"
                  values={{ a: datasharinglink }}
                />
              </LegalOrderedList>

              <LegalParagraph tx="privacy_policy_oh_personal_california_info_p2" />

              <LegalHeading
                level="i" // II.C.iii
                tx="privacy_policy_oh_personal_california_rights_heading"
              />
              <LegalParagraph tx="privacy_policy_oh_personal_california_rights_p1" />

              <LegalOrderedList>
                <LegalListItem tx="privacy_policy_oh_personal_california_rights_know_heading">
                  <LegalParagraph tx="privacy_policy_oh_personal_california_rights_know_p1" />

                  <LegalOrderedList indent="double">
                    <LegalListItem
                      marker="circle"
                      tx="privacy_policy_oh_personal_california_rights_know_item1"
                    />
                    <LegalListItem
                      marker="circle"
                      tx="privacy_policy_oh_personal_california_rights_know_item2"
                    />
                    <LegalListItem
                      marker="circle"
                      tx="privacy_policy_oh_personal_california_rights_know_item3"
                    />
                    <LegalListItem
                      marker="circle"
                      tx="privacy_policy_oh_personal_california_rights_know_item4"
                    />
                    <LegalListItem
                      marker="circle"
                      tx="privacy_policy_oh_personal_california_rights_know_item5"
                    />
                    <LegalListItem
                      marker="circle"
                      tx="privacy_policy_oh_personal_california_rights_know_item6"
                    />
                  </LegalOrderedList>
                </LegalListItem>

                <LegalListItem tx="privacy_policy_oh_personal_california_rights_correction_heading">
                  <LegalParagraph tx="privacy_policy_oh_personal_california_rights_correction_p1_oct_2024" />
                </LegalListItem>

                <LegalListItem tx="privacy_policy_oh_personal_california_rights_deletion_heading">
                  <LegalParagraph tx="privacy_policy_oh_personal_california_rights_deletion_p1" />
                </LegalListItem>

                <LegalListItem tx="privacy_policy_oh_personal_california_rights_requests_heading">
                  <LegalParagraph tx="privacy_policy_oh_personal_california_rights_request_p1" />

                  <div className="ml-12">
                    <LegalParagraph tx="privacy_policy_oh_personal_california_rights_request_detail_p1" />
                    <LegalParagraph tx="privacy_policy_oh_personal_california_rights_request_detail_p2" />
                    <LegalParagraph tx="privacy_policy_oh_personal_california_rights_request_detail_p3" />
                  </div>
                </LegalListItem>

                <LegalListItem tx="privacy_policy_oh_personal_california_right_to_appeal_title">
                  <LegalParagraph tx="privacy_policy_oh_personal_california_right_to_appeal_p1" />
                </LegalListItem>

                <LegalListItem tx="privacy_policy_oh_personal_california_rights_discrimination_heading">
                  <LegalParagraph tx="privacy_policy_oh_personal_california_rights_discrimination_p1" />

                  <LegalOrderedList>
                    <LegalListItem tx="privacy_policy_oh_personal_california_rights_discrimination_item1" />
                    <LegalListItem tx="privacy_policy_oh_personal_california_rights_discrimination_item2" />
                    <LegalListItem tx="privacy_policy_oh_personal_california_rights_discrimination_item3" />
                    <LegalListItem tx="privacy_policy_oh_personal_california_rights_discrimination_item4" />
                  </LegalOrderedList>
                </LegalListItem>
              </LegalOrderedList>
            </LegalHeading>

            <LegalHeading
              level="A" // II.D
              tx="privacy_policy_oh_personal_data_heading"
              id="data-sharing-and-disclosures-section"
            >
              <LegalHeading
                level="i" // II.D.i
                tx="privacy_policy_oh_personal_data_sharing_heading"
              />
              <LegalParagraph tx="privacy_policy_oh_personal_data_sharing_p1" />
              <LegalParagraph tx="privacy_policy_oh_personal_data_sharing_p2" />

              <LegalOrderedList>
                <LegalListItem tx="privacy_policy_oh_personal_data_sharing_item1" />
                <LegalListItem tx="privacy_policy_oh_personal_data_sharing_item2" />
                <LegalListItem tx="privacy_policy_oh_personal_data_sharing_item3" />
                <LegalListItem tx="privacy_policy_oh_personal_data_sharing_item4" />
                <LegalListItem tx="privacy_policy_oh_personal_data_sharing_item5" />
              </LegalOrderedList>

              <LegalParagraph tx="privacy_policy_personal_data_sharing_p3_oct_2024" />

              <LegalHeading
                level="i" // II.D.ii
                tx="privacy_policy_oh_transfers_heading"
              />
              <LegalParagraph tx="privacy_policy_oh_transfers_p1" />
              <LegalParagraph tx="privacy_policy_oh_transfers_p2" />
              <LegalParagraph
                tx="privacy_policy_oh_transfers_p3"
                values={{ a: dataprivacylink }}
              />
              <LegalParagraph tx="privacy_policy_oh_transfers_p4" />
              <LegalParagraph tx="privacy_policy_oh_transfers_p5" />

              <LegalHeading
                level="i" // II.D.iii
                tx="privacy_policy_oh_personal_data_disclosures_heading"
              />
              <LegalParagraph tx="privacy_policy_oh_personal_data_disclosures_p1" />

              <LegalOrderedList>
                <LegalListItem tx="privacy_policy_oh_personal_data_disclosures_item1" />
                <LegalListItem tx="privacy_policy_oh_personal_data_disclosures_item2" />
                <LegalListItem tx="privacy_policy_oh_personal_data_disclosures_item3" />
                <LegalListItem tx="privacy_policy_oh_personal_data_disclosures_item4" />
              </LegalOrderedList>

              <LegalParagraph tx="privacy_policy_oh_personal_data_disclosures_p2" />
            </LegalHeading>
          </LegalHeading>

          {/* SAFEGUARDING YOUR DATA */}
          <LegalHeading
            level="I" // III
            tx="privacy_policy_oh_safeguarding_heading"
          />
          <LegalParagraph tx="privacy_policy_oh_safeguarding_p1" />
          <LegalParagraph tx="privacy_policy_oh_safeguarding_p2" />
          <LegalParagraph tx="privacy_policy_oh_safeguarding_p3" />
          <LegalParagraph tx="privacy_policy_oh_safeguarding_p4" />

          {/* DATA RETENTION */}
          <LegalHeading
            level="I" // IV
            tx="privacy_policy_oh_retention_heading"
          />
          <LegalParagraph tx="privacy_policy_oh_retention_p1" />
          <LegalParagraph tx="privacy_policy_oh_retention_p2" />
          <LegalParagraph
            tx="privacy_policy_oh_retention_p4"
            values={{ a: supportlink }}
          />

          {/* USE OF COOKIES */}
          <LegalHeading
            level="I" // V
            tx="privacy_policy_oh_cookies_heading"
          />
          <LegalParagraph tx="privacy_policy_oh_cookies_p1" />
          <LegalParagraph tx="privacy_policy_oh_cookies_p2" />
          <LegalParagraph
            tx="privacy_policy_oh_cookies_p3"
            values={{
              a: (chunks: ReactNode) =>
                cookiepolicylink({ chunks, selectedLocale }),
            }}
          />

          {/* YOUR RIGHTS AS A DATA SUBJECT */}
          <LegalHeading
            level="I" // VI
            tx="privacy_policy_oh_rights_heading"
          />
          <LegalParagraph tx="privacy_policy_oh_rights_p1" />

          <LegalOrderedList>
            <LegalListItem tx="privacy_policy_oh_rights_item1">
              <LegalParagraph tx="privacy_policy_oh_rights_item1_p1" />
              <LegalParagraph
                tx="privacy_policy_oh_rights_item1_p2"
                values={{ a: cloudlink }}
              />
            </LegalListItem>
            <LegalListItem tx="privacy_policy_oh_rights_item2">
              <LegalParagraph tx="privacy_policy_oh_rights_item2_p1" />
            </LegalListItem>
            <LegalListItem tx="privacy_policy_oh_rights_item3">
              <LegalParagraph tx="privacy_policy_oh_rights_item3_p1" />
              <LegalParagraph tx="privacy_policy_oh_rights_item3_p2" />
            </LegalListItem>
            <LegalListItem tx="privacy_policy_oh_rights_item4">
              <LegalParagraph tx="privacy_policy_oh_rights_item4_p1" />
              <LegalParagraph tx="privacy_policy_oh_rights_item4_p2" />
            </LegalListItem>
            <LegalListItem tx="privacy_policy_oh_rights_item5">
              <LegalParagraph tx="privacy_policy_oh_rights_item5_p1" />
            </LegalListItem>
            <LegalListItem tx="privacy_policy_oh_rights_item6">
              <LegalParagraph tx="privacy_policy_oh_rights_item6_p1" />
            </LegalListItem>
            <LegalListItem tx="privacy_policy_oh_rights_item7">
              <LegalParagraph tx="privacy_policy_rights_item7_p1_oct_2024" />
            </LegalListItem>
          </LegalOrderedList>

          <LegalParagraph tx="privacy_policy_oh_rights_p2" />
          <LegalParagraph tx="privacy_policy_oh_rights_questions" />
          <LegalParagraph tx="privacy_policy_oh_rights_p3" />

          {/* CONTROLLER CONTACT INFORMATION */}
          <LegalHeading
            level="I" // VII
            tx="privacy_policy_oh_contact_heading"
          />
          <LegalParagraph tx="privacy_policy_oh_contact_p1" />
          <LegalParagraph tx="privacy_policy_oh_contact_ourahealth" />
          <LegalParagraph tx="privacy_policy_oh_contact_ouraring" />

          {/* CHANGES TO THIS PRIVACY NOTICE */}
          <h2 className="uppercase">
            <RichText messageKey="privacy_policy_oh_changes_heading" />
          </h2>
          <LegalParagraph tx="privacy_policy_oh_changes_p1" />
        </TypographyRhythm>
      </LayoutGrid>
    </PageLayout>
  );
};

export default PrivacyPolicyOuraHealth;
